/**
 * ReportingDashboardPage — `/reporting`.
 *
 * Top-level reporting & analytics landing page. Wired to real backend:
 *   - `GET /v1/reporting/dashboard` — aggregated KPI counts (B-slice 5)
 *   - `GET /v1/reporting/runs` — recent run list
 */
import { type JSX } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CalendarClock,
  FileBarChart,
  FileText,
  LayoutDashboard,
  Plus,
  Play,
  Share2,
} from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  getReportingDashboard,
  listReportRuns,
  type ReportingDashboardWire,
  type ReportRunWire,
} from '../../lib/api/reporting';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export function ReportingDashboardPage(): JSX.Element {
  const dashboard = useQuery<ReportingDashboardWire, Error>({
    queryKey: ['reporting', 'dashboard'],
    queryFn: () => getReportingDashboard(),
    retry: false,
    staleTime: 60_000,
  });
  const runs = useQuery<readonly ReportRunWire[], Error>({
    queryKey: ['reporting', 'runs'],
    queryFn: () => listReportRuns(),
    retry: false,
  });

  const denied =
    (dashboard.isError && dashboard.error instanceof ApiError &&
      (dashboard.error.status === 401 || dashboard.error.status === 403)) ||
    (runs.isError && runs.error instanceof ApiError &&
      (runs.error.status === 401 || runs.error.status === 403));

  const d = dashboard.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reporting & Analytics"
        description="Generate, schedule, and manage platform reports."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/reporting/dashboards/builder"
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-nipost-green border border-nipost-green rounded-lg hover:bg-nipost-green-light transition-colors"
            >
              <LayoutDashboard className="w-4 h-4" /> Dashboard builder
            </Link>
            <Link
              to="/reporting/reports/builder"
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors"
            >
              <Plus className="w-4 h-4" /> Custom report
            </Link>
          </div>
        }
      />

      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/readDashboard</code> required (REGIONAL_MANAGER+ / ANALYST).
        </DeferredNotice>
      )}

      {dashboard.isPending && (
        <p className="text-sm text-muted">Loading dashboard…</p>
      )}

      {!denied && !dashboard.isPending && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Report definitions"
            value={(d?.definitionCount ?? 0).toLocaleString('en-US')}
            hint="Published report definitions"
            icon={FileText}
          />
          <KPICard
            title="Recent runs"
            value={(d?.recentRunCount ?? 0).toLocaleString('en-US')}
            hint="Lifetime run count"
            icon={Play}
          />
          <KPICard
            title="Scheduled active"
            value={(d?.scheduledCount ?? 0).toLocaleString('en-US')}
            hint="Active scheduled reports"
            icon={CalendarClock}
          />
          <KPICard
            title="KPI definitions"
            value={(d?.kpiDefinitionCount ?? 0).toLocaleString('en-US')}
            hint="Configured KPIs"
            icon={Activity}
          />
          <KPICard
            title="Alert rules"
            value={(d?.alertCount ?? 0).toLocaleString('en-US')}
            hint="Active alert rules"
            icon={AlertTriangle}
          />
          <KPICard
            title="Cache hit rate"
            value={`${d?.cacheHitRate ?? 0}%`}
            hint="Run cache effectiveness"
            icon={FileBarChart}
          />
        </div>
      )}

      {/* Recent runs */}
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Recent runs</h3>
          <div className="flex items-center gap-2">
            <Link to="/reporting/scheduled" className="text-xs font-semibold text-nipost-green hover:underline">
              Scheduled →
            </Link>
            <Link to="/reporting/access-audit" className="text-xs font-semibold text-nipost-green hover:underline">
              Access audit →
            </Link>
            <Link to="/reporting/dashboards/sharing" className="inline-flex items-center gap-1 text-xs font-semibold text-nipost-green hover:underline">
              <Share2 className="w-3 h-3" /> Sharing
            </Link>
          </div>
        </div>

        {runs.isPending ? (
          <p className="text-sm text-muted">Loading runs…</p>
        ) : denied ? (
          <DeferredNotice title="Permission required">
            <code className="font-mono">Reporting/listRuns</code> required.
          </DeferredNotice>
        ) : runs.isError ? (
          <DeferredNotice title="Service temporarily unavailable">
            {(runs.error as Error).message}
          </DeferredNotice>
        ) : runs.data && runs.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-3 py-2">Report</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Started</th>
                  <th className="text-right px-3 py-2">Duration</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {runs.data.slice(0, 12).map((run) => (
                  <tr key={run.id}>
                    <td className="px-3 py-2 font-mono text-xs">{run.reportKey}</td>
                    <td className="px-3 py-2 text-xs">{run.status}</td>
                    <td className="px-3 py-2 text-xs">{formatDateTime(run.startedAt)}</td>
                    <td className="px-3 py-2 text-right text-xs">{run.durationMs ?? '—'} ms</td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={`/reporting/reports/${encodeURIComponent(run.id)}`}
                        className="text-xs font-semibold text-nipost-green hover:underline"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted">No runs yet. <Link to="/reporting/reports/builder" className="text-nipost-green hover:underline">Build a report →</Link></p>
        )}
      </div>

      {/* Navigation tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {([
          { to: '/reporting/kpi-config', label: 'KPI Config', icon: Activity },
          { to: '/reporting/alerts', label: 'Alerts', icon: AlertTriangle },
          { to: '/reporting/adhoc', label: 'Ad-hoc Query', icon: FileBarChart },
          { to: '/reporting/scheduled', label: 'Scheduled', icon: CalendarClock },
          { to: '/reporting/management-kpis', label: 'Management KPIs', icon: FileText },
          { to: '/reporting/benchmarks', label: 'Benchmarks', icon: Play },
          { to: '/reporting/role-scoped', label: 'Role Dashboards', icon: LayoutDashboard },
          { to: '/reporting/forecasting', label: 'Forecasting', icon: FileBarChart },
        ] as const).map((tile) => (
          <Link
            key={tile.to}
            to={tile.to}
            className="bg-white border border-border rounded-xl p-4 flex flex-col items-center gap-2 hover:border-nipost-green/40 hover:shadow-sm transition-all text-center"
          >
            <tile.icon className="w-6 h-6 text-nipost-green" />
            <span className="text-xs font-medium text-gray-700">{tile.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
